import { z } from 'zod';
import { CreateVenueInputSchema } from './venue.js';

/**
 * F11 venue-claim types + Zod schemas.
 *
 * Decisions: see plans/11_F11_b2b_account_venue_claim.md (Decisions 1, 4, 6, 8, 15).
 *
 * ─── Naming note ─────────────────────────────────────────────────────────────
 *
 * F10's `venue.ts` exports `VenueClaimStatus` for the column-level state on the
 * `venues` table (`'unclaimed' | 'claim_pending' | 'claimed'`). This file's
 * `VenueClaimStatus` is the F11 entity-level lifecycle on the `venue_claims`
 * row. The two are intentionally distinct concepts; the index barrel re-exports
 * this one as `VenueClaimEntityStatus` (+ `VenueClaimEntityStatusSchema`) to
 * avoid a top-level collision with F10's column-level type.
 *
 * ─── VenueClaimStatus lifecycle ──────────────────────────────────────────────
 *
 *   draft → pending (user submits)
 *   pending → approved (admin decides; venue → tier_2 + claimed)
 *   pending → rejected (admin decides; venue → unclaimed)
 *   pending → withdrawn (user self-service withdraw; venue → unclaimed)
 *   approved → revoked_by_admin (admin reversal; venue → tier_1 + unclaimed)
 *
 * Partial unique index `venue_claims_one_pending_per_venue` enforces at-most
 * one row in `status='pending'` per venue at any time. Second claimant gets
 * 409 CLAIM_ALREADY_PENDING.
 */

// ─── Enum constants ──────────────────────────────────────────────────────────

export const VENUE_CLAIM_ENTITY_STATUSES = [
  'draft',
  'pending',
  'approved',
  'rejected',
  'withdrawn',
  'revoked_by_admin',
] as const;
export type VenueClaimStatus = (typeof VENUE_CLAIM_ENTITY_STATUSES)[number];

export const VENUE_CLAIM_DOCUMENT_TYPES = [
  'commercial_register',
  'utility_bill',
  'selfie_on_site',
  'id_document',
  'other',
] as const;
export type VenueClaimDocumentType = (typeof VENUE_CLAIM_DOCUMENT_TYPES)[number];

export const VENUE_CLAIM_DOC_MIMES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/webp',
] as const;
export type VenueClaimDocMime = (typeof VENUE_CLAIM_DOC_MIMES)[number];

export const VENUE_CLAIM_DOC_MAX_BYTES = 10 * 1024 * 1024; // 10 MB
export const VENUE_CLAIM_DOC_MAX_COUNT = 10;

// ─── Raw DB row shapes ───────────────────────────────────────────────────────

export type VenueClaim = {
  readonly id: string;
  readonly venueId: string;
  readonly b2bAccountId: string;
  readonly claimantUserId: string;
  readonly status: VenueClaimStatus;
  readonly submittedAt: string | null;
  readonly decidedAt: string | null;
  readonly decidedByAdminId: string | null;
  readonly rejectionReason: string | null;
  readonly withdrawnAt: string | null;
  readonly siteVisitNotes: string | null;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/**
 * F61 (B19) — a row of `GET /me/venue-claims`: the claim plus the venue's
 * display name (from one join), so the claims page doesn't fetch every venue.
 * `null` when the venue row is gone.
 */
export type MyVenueClaim = VenueClaim & {
  readonly venueName: string | null;
};

export type VenueClaimDocument = {
  readonly id: string;
  readonly claimId: string;
  readonly documentType: VenueClaimDocumentType;
  readonly s3Key: string;
  readonly sha256: string;
  readonly fileName: string;
  readonly mimeType: string;
  readonly sizeBytes: number;
  readonly uploadedAt: string;
};

// ─── Zod schemas ─────────────────────────────────────────────────────────────

export const VenueClaimStatusSchema = z.enum(VENUE_CLAIM_ENTITY_STATUSES);
export const VenueClaimDocumentTypeSchema = z.enum(VENUE_CLAIM_DOCUMENT_TYPES);
export const VenueClaimDocMimeSchema = z.enum(VENUE_CLAIM_DOC_MIMES);

/**
 * Create-claim input — discriminated union of two branches:
 *   1. Claim an existing venue from the catalog → `{ venueId: <uuid> }`
 *   2. Add a new venue inline (F10 venue-create payload) → `{ newVenue: {...} }`
 *
 * Both members are `.strict()` so payloads carrying both keys (or unknown keys)
 * are rejected — the wizard MUST pick exactly one branch.
 */
export const CreateVenueClaimInputSchema = z.union([
  z.object({ venueId: z.string().uuid() }).strict(),
  z.object({ newVenue: CreateVenueInputSchema }).strict(),
]);
export type CreateVenueClaimInput = z.infer<typeof CreateVenueClaimInputSchema>;

export const AttachVenueClaimDocumentInputSchema = z
  .object({
    documentType: VenueClaimDocumentTypeSchema,
    fileName: z.string().min(1).max(255),
    mimeType: VenueClaimDocMimeSchema,
    sizeBytes: z.number().int().positive().max(VENUE_CLAIM_DOC_MAX_BYTES),
  })
  .strict();
export type AttachVenueClaimDocumentInput = z.infer<typeof AttachVenueClaimDocumentInputSchema>;

export const SubmitVenueClaimInputSchema = z.object({}).strict();
export type SubmitVenueClaimInput = z.infer<typeof SubmitVenueClaimInputSchema>;

export const WithdrawVenueClaimInputSchema = z.object({}).strict();
export type WithdrawVenueClaimInput = z.infer<typeof WithdrawVenueClaimInputSchema>;

export const ApproveVenueClaimInputSchema = z
  .object({
    adminOverrideNoDocs: z.boolean().optional(),
    reason: z.string().max(500).optional(),
  })
  .strict();
export type ApproveVenueClaimInput = z.infer<typeof ApproveVenueClaimInputSchema>;

export const RejectVenueClaimInputSchema = z
  .object({
    reason: z.string().min(10).max(500),
  })
  .strict();
export type RejectVenueClaimInput = z.infer<typeof RejectVenueClaimInputSchema>;

export const ReassignVenueOwnershipInputSchema = z
  .object({
    newOwnerB2bAccountId: z.string().uuid().nullable(),
    reason: z.string().min(10).max(500),
  })
  .strict();
export type ReassignVenueOwnershipInput = z.infer<typeof ReassignVenueOwnershipInputSchema>;

export const VenueClaimSiteVisitNotesInputSchema = z
  .object({
    siteVisitNotes: z.string().max(2000).nullable(),
  })
  .strict();
export type VenueClaimSiteVisitNotesInput = z.infer<typeof VenueClaimSiteVisitNotesInputSchema>;
